import type { RiProblem } from './ri-problems';

/** What a state notice says, and what it offers to do next (MASTER-SPEC §14.4: text, an icon and a next step). */
export interface NoticeCopy {
  /** A plain glyph, always decorative: the title says what it means. */
  icon: string;
  title: string;
  description: string;
  /** The next step the notice offers. */
  next: 'retry' | 'settings' | 'none';
}

/** Where an administrator switches Repository Intelligence on. */
export const SETTINGS_PATH = '/settings/repository-intelligence';

/**
 * The screens' own words for every problem. `manage`: the person can change the organization's settings, so a
 * switched-off feature offers them the way there instead of "ask an administrator".
 */
export function problemCopy(problem: RiProblem, manage: boolean): NoticeCopy {
  const switchedOff = (title: string): NoticeCopy => ({
    icon: '⊘',
    title,
    description: manage
      ? 'You can switch it on in the Repository Intelligence settings.'
      : 'Ask an administrator to switch it on.',
    next: manage ? 'settings' : 'none',
  });
  switch (problem) {
    case 'disabled':
      return switchedOff('Repository Intelligence is not enabled for your organization');
    case 'ask-disabled':
      return switchedOff('Questions about code are not enabled for your organization');
    case 'analyze-disabled':
      return switchedOff('Change analysis is not enabled for your organization');
    case 'not-configured':
      return {
        icon: '⊘',
        title: 'Repository Intelligence is not enabled for your organization',
        description:
          'This Desk server is not set up to reach Repository Intelligence. An operator has to configure it first.',
        next: 'none',
      };
    case 'forbidden':
      return {
        icon: '⊗',
        title: 'You don’t have access to Repository Intelligence',
        description:
          'It is available to the system Super Admin only. Ask an administrator if you need it.',
        next: 'none',
      };
    case 'signed-out':
      return {
        icon: '⊗',
        title: 'Your session has ended',
        description: 'Sign in again to continue.',
        next: 'none',
      };
    case 'link-not-found':
      return {
        icon: '?',
        title: 'This repository link does not exist',
        description: 'It may have been unlinked from the project.',
        next: 'none',
      };
    case 'no-registration':
    case 'not-found':
      return {
        icon: '○',
        title: 'Repository Intelligence does not have this',
        description: 'The repository is not indexed, or this is no longer in its index.',
        next: 'none',
      };
    case 'conflict':
      return {
        icon: '⧗',
        title: 'The repository is not available right now',
        description:
          'It is being prepared, or its copy is being checked. Try again in a few minutes.',
        next: 'retry',
      };
    case 'rate-limited':
      return {
        icon: '⧗',
        title: 'Too many requests',
        description:
          'Wait a minute, then try again. If the usage limit was reached, it resets later.',
        next: 'retry',
      };
    case 'unavailable':
      return {
        icon: '↻',
        title: 'Repository Intelligence is unavailable right now',
        description: 'Desk could not get an answer from it. Your request was not lost; try again.',
        next: 'retry',
      };
    case 'invalid':
      return {
        icon: '!',
        title: 'The request was not accepted',
        description: 'Check what you entered and try again.',
        next: 'none',
      };
    case 'unprocessable':
      return {
        icon: '!',
        title: 'This could not be analysed',
        description:
          'The commits could not be found, the task has no commits in this repository, or the change is too large.',
        next: 'none',
      };
    case 'cancelled':
      return {
        icon: '!',
        title: 'Nothing was changed',
        description: 'The password confirmation was cancelled.',
        next: 'none',
      };
    case 'unknown':
      return {
        icon: '!',
        title: 'Something went wrong',
        description: 'Try again. If it keeps happening, tell an administrator.',
        next: 'retry',
      };
  }
}

/** A problem in a word or two, for a table cell; the full notice is on the repository's own page. */
export function problemShortLabel(problem: RiProblem): string {
  switch (problem) {
    case 'disabled':
    case 'not-configured':
      return 'Not enabled';
    case 'forbidden':
    case 'signed-out':
      return 'No access';
    case 'link-not-found':
      return 'Unlinked';
    case 'rate-limited':
      return 'Busy, try later';
    default:
      return 'Unavailable';
  }
}
